<template>
  <div>
    <div
      class="text-h6 text-sm-h4 my-4 d-flex justify-space-between align-center"
    >
      Видео и материалы
      <v-btn color="primary" @click="openCreate">
        <v-icon left>mdi-plus</v-icon>
        Добавить
      </v-btn>
    </div>

    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>Видео</v-tab>
      <v-tab>Презентации</v-tab>
      <v-tab>Отзывы</v-tab>
    </v-tabs>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :loading="loading"
      no-data-text="Нет записей"
    >
      <template v-slot:[`item.active`]="{ item }">
        <v-icon :color="item.active ? 'success' : 'grey'">
          {{ item.active ? "mdi-check-circle" : "mdi-close-circle" }}
        </v-icon>
      </template>
      <template v-slot:[`item.filePath`]="{ item }">
        <a
          v-if="item.filePath"
          :href="item.filePath"
          target="_blank"
          rel="noopener"
        >
          Скачать
        </a>
        <span v-else-if="item.link">
          <a :href="item.link" target="_blank" rel="noopener">Ссылка</a>
        </span>
        <span v-else class="grey--text">—</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn icon small @click="openEdit(item)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="confirmDelete(item)">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Диалог создания / редактирования -->
    <v-dialog v-model="dialog" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ editId ? "Редактировать" : "Добавить" }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="form.type"
            :items="typeOptions"
            label="Тип *"
            outlined
            dense
            class="mb-2"
          />
          <v-select
            v-model="form.audience"
            :items="audienceOptions"
            label="Аудитория *"
            outlined
            dense
            class="mb-2"
          />
          <v-text-field
            v-model="form.title"
            label="Заголовок *"
            outlined
            dense
            class="mb-2"
          />
          <v-textarea
            v-model="form.description"
            label="Описание"
            outlined
            dense
            rows="3"
            class="mb-2"
          />
          <v-text-field
            v-if="form.type !== 'presentations'"
            v-model="form.link"
            label="Ссылка на видео"
            outlined
            dense
            class="mb-2"
          />
          <div v-if="form.type === 'presentations'" class="mb-2">
            <div
              v-if="editId && currentFilePath"
              class="mb-1 d-flex align-center"
            >
              <a
                :href="currentFilePath"
                target="_blank"
                rel="noopener"
                class="mr-2"
              >
                Текущий файл
              </a>
              <v-btn x-small text color="error" @click="removeCurrentFile">
                Удалить
              </v-btn>
            </div>
            <v-file-input
              v-model="form.file"
              label="Файл презентации"
              outlined
              dense
              accept=".pdf,.ppt,.pptx,.key"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
            />
          </div>
          <v-text-field
            v-model.number="form.sortOrder"
            label="Порядок сортировки"
            outlined
            dense
            type="number"
            class="mb-2"
          />
          <v-switch v-model="form.active" label="Активно" dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Отмена</v-btn>
          <v-btn color="primary" :loading="saving" @click="save"
            >Сохранить</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Диалог подтверждения удаления -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Удалить запись?</v-card-title>
        <v-card-text>Это действие необратимо.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deleting" @click="doDelete"
            >Удалить</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" top :timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import NSApi from "@/api/NSApi";

const TYPE_LABELS = {
  video: "Видео",
  presentations: "Презентации",
  reviews: "Отзывы",
};
const TABS = ["video", "presentations", "reviews"];

const emptyForm = () => ({
  type: "video",
  audience: "adults",
  title: "",
  description: "",
  link: "",
  file: null,
  sortOrder: 0,
  active: true,
});

export default {
  name: "AdminMaterialsView",
  data() {
    return {
      items: [],
      loading: false,
      activeTab: 0,
      dialog: false,
      editId: null,
      currentFilePath: null,
      deleteFilePending: false,
      form: emptyForm(),
      saving: false,
      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      typeOptions: [
        { text: "Видео", value: "video" },
        { text: "Презентации", value: "presentations" },
        { text: "Отзывы", value: "reviews" },
      ],
      audienceOptions: [
        { text: "Взрослые", value: "adults" },
        { text: "Дети", value: "children" },
      ],
      headers: [
        { text: "Заголовок", value: "title" },
        { text: "Описание", value: "description", sortable: false },
        { text: "Файл / ссылка", value: "filePath", sortable: false },
        { text: "Порядок", value: "sortOrder", width: 90 },
        { text: "Активно", value: "active", width: 80 },
        { text: "", value: "actions", sortable: false, width: 90 },
      ],
    };
  },
  computed: {
    filteredItems() {
      const type = TABS[this.activeTab];
      return this.items
        .filter((i) => i.type === type)
        .map((i) => ({
          ...i,
          typeLabel: TYPE_LABELS[i.type] || i.type,
          description: i.description
            ? i.description.length > 80
              ? i.description.slice(0, 80) + "…"
              : i.description
            : "—",
        }));
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.items = await NSApi.instance.getMaterials({ active: undefined });
      } catch (e) {
        this.showSnackbar("Ошибка загрузки", "error");
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editId = null;
      this.currentFilePath = null;
      this.deleteFilePending = false;
      this.form = { ...emptyForm(), type: TABS[this.activeTab] };
      this.dialog = true;
    },
    openEdit(item) {
      this.editId = item.id;
      this.currentFilePath = item.filePath || null;
      this.deleteFilePending = false;
      this.form = {
        type: item.type,
        audience: item.audience || "adults",
        title: item.title,
        description: item.description === "—" ? "" : item.description || "",
        link: item.link || "",
        file: null,
        sortOrder: item.sortOrder || 0,
        active: item.active,
      };
      // Restore full description from original item
      const original = this.items.find((i) => i.id === item.id);
      if (original) this.form.description = original.description || "";
      this.dialog = true;
    },
    removeCurrentFile() {
      this.deleteFilePending = true;
      this.currentFilePath = null;
    },
    closeDialog() {
      this.dialog = false;
      this.form = emptyForm();
      this.editId = null;
    },
    async save() {
      if (!this.form.type) return this.showSnackbar("Укажите тип", "error");
      if (!this.form.title.trim())
        return this.showSnackbar("Укажите заголовок", "error");

      this.saving = true;
      try {
        const payload = { ...this.form };
        if (this.deleteFilePending) payload.deleteFile = true;

        if (this.editId) {
          await NSApi.instance.updateMaterial(this.editId, payload);
        } else {
          await NSApi.instance.createMaterial(payload);
        }
        this.closeDialog();
        await this.load();
        this.showSnackbar("Сохранено");
      } catch (e) {
        this.showSnackbar("Ошибка сохранения", "error");
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async doDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        await NSApi.instance.deleteMaterial(this.deleteTarget.id);
        this.deleteDialog = false;
        this.deleteTarget = null;
        await this.load();
        this.showSnackbar("Удалено");
      } catch (e) {
        this.showSnackbar("Ошибка удаления", "error");
      } finally {
        this.deleting = false;
      }
    },
    showSnackbar(text, color = "success") {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>
